import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from '../contexts/AuthContext'
import mixpanelService from '../services/mixpanel'

export const usePools = () => {
  const [pools, setPools] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuth()

  const fetchPools = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (!user) {
        setPools([])
        return
      }

      const { data, error } = await supabase
        .from('pools')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true })

      if (error) throw error
      setPools(data || [])
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  const createPool = async (poolData) => {
    try {
      const { data, error } = await supabase
        .from('pools')
        .insert([
          {
            ...poolData,
            user_id: user.id,
          },
        ])
        .select()

      if (error) throw error
      setPools([...pools, data[0]])
      
      // Track pool creation funnel step
      mixpanelService.trackFunnelStep('create_pool', {
        pool_id: data[0].id,
        pool_type: data[0].type,
        pool_name: data[0].name,
        user_id: user.id
      })
      
      return { data: data[0], error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const updatePool = async (poolId, poolData) => {
    try {
      const { data, error } = await supabase
        .from('pools')
        .update(poolData)
        .eq('id', poolId)
        .eq('user_id', user.id)
        .select()

      if (error) throw error
      setPools(pools.map(pool => pool.id === poolId ? data[0] : pool))
      return { data: data[0], error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const deletePool = async (poolId) => {
    try {
      const { error } = await supabase
        .from('pools')
        .update({ is_active: false })
        .eq('id', poolId)
        .eq('user_id', user.id)

      if (error) throw error
      setPools(pools.filter(pool => pool.id !== poolId))
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  useEffect(() => {
    fetchPools()
  }, [fetchPools])

  return {
    pools,
    loading,
    error,
    createPool,
    updatePool,
    deletePool,
    refetch: fetchPools,
  }
}